import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Box, ListItemText, MenuItem, Select } from "@mui/material";
import LanguageContext from "components/LanguageContext";
import PropTypes from "prop-types";
import React, { useContext, useRef, useState } from "react";
import { getTranslateIndex } from "utils/commonUtils";
import t from "utils/locales/translation.json";

const allButtonValue = "all_tags";

const SelectTagsBlock = (props) => {
  const { label, allTags = [], selectedTags = [], handleSelectTagsForm = null } = props;
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef(null);

  const { language } = useContext(LanguageContext);

  if (allTags?.length === 0) {
    return null;
  }

  const handleSelectAllTags = () => {
    if (selectedTags.length === 0) {
      handleSelectTagsForm(allTags);
    } else {
      handleSelectTagsForm([]);
    }
  };

  const handleSelectTags = (event) => {
    const {
      target: { value },
    } = event;

    if (value.includes(allButtonValue)) {
      handleSelectAllTags();
    } else {
      handleSelectTagsForm(value);
    }
  };

  const handleMenuListKeyDown = (event) => {
    // Tab ferme la liste comme Echap et conserve le focus sur le trigger du Select.
    if (event.key === "Tab") {
      event.preventDefault();
      setIsSelectOpen(false);
      window.requestAnimationFrame(() => {
        selectRef.current?.focus();
      });
      return;
    }

    if (event.key !== " " && event.key !== "Spacebar") {
      return;
    }

    const option = event.target.closest('[role="option"]');

    if (!option) {
      return;
    }

    event.preventDefault();
    option.click();
  };

  return (
    <div className="fr-select-group">
      <label className="fr-label fr-text--bold" htmlFor={`select-${label}`}>
        {label}
      </label>
      <div aria-label={t[language].all_news.tag_list} />
      {/* Le composant Select de MUI ne gère pas correctement le clic sur les options du menu avec la touche "Espace" */}
      {/* (contrairement à la touche "Entrée"). La fonction handleMenuListKeyDown permet de pallier ce problème */}
      {/* d'accessibilité en déclenchant le clic sur l'option ciblée lorsque l'utilisateur appuie sur la touche "Espace". */}
      {/* En attente du composant "Liste déroulante riche" du DSFR " */}
      <Select
        ref={selectRef}
        name="options"
        multiple
        open={isSelectOpen}
        displayEmpty
        label={t[language].all_news.empty_placeholder}
        variant="standard"
        className="fr-select"
        value={selectedTags}
        renderValue={(tagsSelected) => {
          if (tagsSelected.length === 0) {
            return t[language].all_news.empty_placeholder;
          }
          return `${tagsSelected.length} ${
            t[language].all_news.filled_placeholder[getTranslateIndex(tagsSelected.length)]
          }`;
        }}
        inputProps={{ id: `select-${label}`, sx: { "&:focus": { outline: "2px solid #0a76f6" } } }}
        MenuProps={{
          MenuListProps: {
            onKeyDown: handleMenuListKeyDown,
            sx: {
              px: 1,
              maxHeight: 500,
              "&.fr-fieldset__element": { mb: 0, pb: 2 },
            },
          },
        }}
        sx={{
          p: 0,
          height: 40,
          "& .MuiSelect-select": { pl: 2, pt: 1.125, pb: 1.25 },
          "& svg": { display: "none" },
        }}
        onOpen={() => setIsSelectOpen(true)}
        onClose={() => setIsSelectOpen(false)}
        onChange={handleSelectTags}
      >
        <MenuItem role="option" value={allButtonValue} sx={{ p: 0 }}>
          <Box
            component="span"
            className={`fr-btn fr-icon-${
              selectedTags.length === 0 ? "check" : "close-circle"
            }-line fr-btn--icon-left fr-btn--tertiary`}
            sx={{ justifyContent: "center", width: "100%" }}
          >
            {selectedTags.length === 0 ? t[language].all_news.select_all : t[language].all_news.deselect_all}
          </Box>
        </MenuItem>
        {allTags
          .sort((a, b) => a.localeCompare(b))
          .map((tag) => {
            const selected = selectedTags.includes(tag);
            const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

            return (
              <MenuItem
                key={tag}
                value={tag}
                role="option"
                sx={{ p: 0, "& span:first-letter": { textTransform: "uppercase" } }}
              >
                <SelectionIcon
                  fontSize="small"
                  style={{ marginRight: 8, padding: 9, boxSizing: "content-box" }}
                  color="primary"
                />
                <ListItemText primary={tag} />
              </MenuItem>
            );
          })}
      </Select>
    </div>
  );
};

SelectTagsBlock.propTypes = {
  label: PropTypes.string.isRequired,
  allTags: PropTypes.arrayOf(PropTypes.string),
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  handleSelectTagsForm: PropTypes.func,
};

export default SelectTagsBlock;
